import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Car, CheckCircle, Clock, CreditCard, Loader2, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS = [
  { key: 'submitted', label: 'Request Submitted', icon: CheckCircle },
  { key: 'review', label: 'Under Review', icon: Clock },
  { key: 'payment', label: 'Secure Payment', icon: CreditCard },
  { key: 'confirmed', label: 'Confirmed', icon: Car },
];

const statusToStep = (status) => {
  switch (status) {
    case 'pending_review':
    case 'pending':
      return 2;
    case 'approved_awaiting_payment':
      return 3;
    case 'confirmed':
    case 'in_progress':
      return 4;
    case 'completed':
      return 5;
    default:
      return 2;
  }
};

const StatusPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const res = await axios.get(`${API}/bookings/${bookingId}`);
        setBooking(res.data);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [bookingId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
        <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
        <div className="text-center">
          <p className="text-white mb-4">We couldn't find that booking.</p>
          <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-600 text-white">Back to Home</Button>
        </div>
      </div>
    );
  }

  const isDeclined = booking.status === 'declined' || booking.status === 'cancelled';
  const isExpired = booking.status === 'expired';
  const currentStep = statusToStep(booking.status);
  const awaitingPayment = booking.status === 'approved_awaiting_payment';

  return (
    <div className="min-h-screen py-16 px-6" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }} data-testid="status-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Your Inspection Status
          </h1>
          <p className="text-zinc-400">{booking.carYear} {booking.carMake} {booking.carModel}</p>
        </div>

        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-8">
          {isDeclined ? (
            <div className="text-center py-6" data-testid="status-declined">
              <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Request Not Approved</h2>
              <p className="text-zinc-400">Unfortunately we couldn't proceed with this request. Please contact us if you have questions.</p>
            </div>
          ) : isExpired ? (
            <div className="text-center py-6" data-testid="status-expired">
              <div className="w-16 h-16 rounded-full bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Payment Link Expired</h2>
              <p className="text-zinc-400 mb-6">This payment link expired because it wasn't completed within 24 hours. If you're still interested, submit a new request and we'll send a fresh link.</p>
              <Button
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                onClick={() => navigate('/book')}
                data-testid="status-request-again-btn"
              >
                Request Again
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {STEPS.map((step, idx) => {
                const stepNum = idx + 1;
                const done = stepNum < currentStep;
                const active = stepNum === currentStep;
                const Icon = step.icon;
                return (
                  <div key={step.key} className="flex items-center gap-4" data-testid={`status-step-${step.key}`}>
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 border transition-all ${
                      done ? 'bg-green-500/15 border-green-500/40 text-green-400'
                        : active ? 'bg-orange-500/15 border-orange-500/50 text-orange-400'
                        : 'bg-zinc-800/40 border-zinc-700 text-zinc-500'
                    }`}>
                      {done ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <div className="flex-1">
                      <p className={`font-semibold ${done || active ? 'text-white' : 'text-zinc-500'}`}>{step.label}</p>
                      {active && (
                        <p className="text-sm text-orange-400">
                          {step.key === 'review' && "We're verifying your request. You'll get a payment link by email."}
                          {step.key === 'payment' && 'Complete your secure payment to confirm your appointment.'}
                          {step.key === 'confirmed' && 'Your appointment is confirmed. See you soon!'}
                          {step.key === 'submitted' && 'Received!'}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {awaitingPayment && booking.paymentLink && (
            <div className="mt-8 pt-6 border-t border-zinc-800 text-center">
              <Button
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold"
                onClick={() => { window.location.href = booking.paymentLink; }}
                data-testid="status-pay-now-btn"
              >
                Pay ${(booking.totalAmount || 0).toFixed(2)} & Confirm Booking
              </Button>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-zinc-800 grid grid-cols-2 gap-y-3 text-sm">
            <span className="text-zinc-500">Location</span>
            <span className="text-zinc-300 text-right">{booking.location}</span>
            <span className="text-zinc-500">Requested</span>
            <span className="text-zinc-300 text-right">{booking.preferredDate} at {booking.preferredTime}</span>
            <span className="text-zinc-500">Total</span>
            <span className="text-orange-400 font-semibold text-right">${(booking.totalAmount || 0).toFixed(2)}</span>
          </div>
        </div>

        <div className="text-center mt-8">
          <Button variant="ghost" className="text-zinc-400 hover:text-white hover:bg-white/5" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StatusPage;
